import { useEffect, useState } from 'react';
import { downloadWithAuth } from '../../api/authInterceptor';
import { VaultFile, fetchVaultFiles, vaultDownloadUrl } from '../../api/catalogApi';

function humanSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${i === 0 ? n : n.toFixed(1)} ${units[i]}`;
}

export default function VaultPanel() {
  const [files, setFiles] = useState<VaultFile[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVaultFiles()
      .then(setFiles)
      .catch(() => setError('Could not load the vault. Is the backend running?'));
  }, []);

  return (
    <div className="split">
      <section className="panel">
        <h2>Downloads</h2>
        <div className="vlist">
          {files?.map((f) => (
            <div className="vitem" key={f.name}>
              <div className="vrow">
                <div className="vic">
                  <svg viewBox="0 0 24 24">
                    <rect x="4" y="3" width="16" height="18" rx="2" />
                    <path d="M9 8h6M9 12h6M9 16h4" />
                  </svg>
                </div>
                <div className="vmeta">
                  <b>{f.name}</b>
                  <span>{humanSize(f.bytes)}</span>
                </div>
                <button
                  className="btn primary vdl"
                  onClick={() =>
                    downloadWithAuth(vaultDownloadUrl(f.name), f.name).catch(() =>
                      setError('Could not download that file. Try signing in again.'),
                    )
                  }
                >
                  Download
                </button>
                <button
                  className="btn vdl"
                  title="If the direct download is blocked, use this instead."
                  onClick={() =>
                    downloadWithAuth(vaultDownloadUrl(f.name, true), `${f.name}.txt`).catch(() =>
                      setError('Could not download that file. Try signing in again.'),
                    )
                  }
                >
                  Download (blocked?)
                </button>
              </div>
              <details className="vhelp">
                <summary>If '{f.name}' download is blocked — how to restore it</summary>
                <ol>
                  <li>
                    Click <b>Download (blocked?)</b> — you'll get <code>{f.name}.txt</code>, a plain-text file
                    (not an executable), which shouldn't trip the same block.
                  </li>
                  <li>
                    Open <b>Command Prompt</b> in the folder where it downloaded (type <code>cmd</code> in the
                    folder's address bar and press Enter).
                  </li>
                  <li>
                    Run: <code>certutil -decode "{f.name}.txt" "{f.name}"</code>
                  </li>
                  <li>
                    <code>{f.name}</code> now appears in that folder, identical to the original.{' '}
                    <code>certutil</code> is built into Windows, so there is nothing to install.
                  </li>
                </ol>
              </details>
            </div>
          ))}
        </div>
        {error && <p className="note">{error}</p>}
        {files && files.length === 0 && (
          <p className="note">No files yet. Add files to backend/vault/ and they appear here.</p>
        )}
      </section>
      <aside className="panel">
        <h2>About</h2>
        <dl>
          <div>
            <dt>Section</dt>
            <dd>AHK AutoHotkey</dd>
          </div>
          <div>
            <dt>Files</dt>
            <dd>{files?.length ?? '–'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd className="status">
              <i className="dot" />
              Ready
            </dd>
          </div>
        </dl>
      </aside>
    </div>
  );
}
